import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useAuth } from '../../context/AuthProvider';
import { qcResultAPI, qcSessionAPI } from '../../services';
import { SOURCE_KIND_LABELS } from './QcResultsListTab';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ArrowLeft, CheckCircle, Play, Edit, Trash2, XCircle, Clock } from 'lucide-react';

const SESSION_TYPE_LABELS = { AUTO: 'Tự động', MANUAL: 'Thủ công' };
const SESSION_STATUS_LABELS = { OPEN: 'Chờ nhận', IN_PROGRESS: 'Đang soi', COMPLETED: 'Đã xong' };
const SESSION_STATUS_VARIANTS = { OPEN: 'warning', IN_PROGRESS: 'primary', COMPLETED: 'success' };
const QC_STATUS_LABELS = { PASSED: 'Đạt', FAILED: 'Không đạt', IN_REVIEW: 'Đang review', PENDING: 'Chờ review' };
const QC_REVIEW_RESULT_LABELS = { APPROVED: 'Chấp nhận', REJECTED_AND_FIXED: 'Từ chối và sửa' };

const formatDate = (v) =>
  !v
    ? '-'
    : (() => {
        try {
          return format(new Date(v), 'dd/MM/yyyy');
        } catch {
          return v;
        }
      })();

export default function QcSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [reviewedTasks, setReviewedTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [stats, setStats] = useState({ passed: 0, failed: 0, pending: 0, in_review: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdminOrManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        setLoadingSession(true);
        const res = await qcSessionAPI.getQcSessionById(sessionId);
        setSession(res?.data ?? res ?? null);
      } catch (e) {
        console.error(e);
        toast.error('Không tải được session');
        setSession(null);
      } finally {
        setLoadingSession(false);
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    loadTasks(1);
  }, [sessionId]);

  const loadTasks = async (page = 1) => {
    try {
      setLoadingTasks(true);
      const res = await qcResultAPI.getQcResults({ session_id: sessionId, page, limit: 10 });
      setReviewedTasks(Array.isArray(res?.data) ? res.data : []);
      setStats(res?.stats ?? { passed: 0, failed: 0, pending: 0, in_review: 0 });
      setPagination(res?.meta ?? { page: 1, limit: 10, total: 0, total_pages: 0 });
    } catch (e) {
      console.error(e);
      toast.error('Không tải được danh sách kết quả QC');
    } finally {
      setLoadingTasks(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) loadTasks(newPage);
  };

  const handleEdit = () => {
    navigate('/qc-sessions');
  };

  const handleDelete = async () => {
    if (!window.confirm('Bạn có chắc muốn xóa session này?')) return;
    try {
      setActionLoading(true);
      await qcSessionAPI.deleteQcSession(sessionId);
      toast.success('Đã xóa session');
      navigate('/qc-sessions');
    } catch (e) {
      toast.error(e?.message ?? 'Không thể xóa session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      setActionLoading(true);
      await qcSessionAPI.joinQcSession(sessionId);
      toast.success('Đã tham gia session');
      const res = await qcSessionAPI.getQcSessionById(sessionId);
      setSession(res?.data ?? res ?? session);
    } catch (e) {
      toast.error(e?.message ?? 'Không thể tham gia session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Bạn có chắc muốn hoàn thành session này?')) return;
    try {
      setActionLoading(true);
      await qcSessionAPI.completeQcSession(sessionId);
      toast.success('Đã hoàn thành session');
      const res = await qcSessionAPI.getQcSessionById(sessionId);
      setSession(res?.data ?? res ?? session);
    } catch (e) {
      toast.error(e?.message ?? 'Không thể hoàn thành session');
    } finally {
      setActionLoading(false);
    }
  };

  if (loadingSession || !session) {
    return (
      <PageLayout>
        <div className="mc-page d-flex align-items-center justify-content-center min-vh-50">
          {loadingSession ? <Spinner animation="border" /> : <p className="text-muted">Không tìm thấy phiên.</p>}
        </div>
      </PageLayout>
    );
  }

  // Phiên gồm 4 nguồn — đếm thiếu nguồn nào là hiện sai số mục (bản cũ chỉ đếm shift_tasks)
  const sessionItemCount =
    (session.shift_tasks?.length || 0) +
    (session.prep_reservations?.length || 0) +
    (session.game_check_sessions?.length || 0) +
    (session.loyalty_participations?.length || 0);

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate('/qc-sessions')}
            >
              <ArrowLeft
                size={18}
                className="me-1"
              />
              Quay lại
            </Button>
            <h1 className="mb-0">Chi tiết phiên QC</h1>
            <Badge bg={SESSION_STATUS_VARIANTS[session.status]}>{SESSION_STATUS_LABELS[session.status]}</Badge>
          </div>
          <div className="d-flex gap-2">
            {session.status === 'OPEN' && sessionItemCount === 0 && isAdminOrManager && (
              <>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={handleEdit}
                  disabled={actionLoading}
                >
                  <Edit
                    size={16}
                    className="me-1"
                  />{' '}
                  Sửa
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  disabled={actionLoading}
                >
                  <Trash2
                    size={16}
                    className="me-1"
                  />{' '}
                  Xóa
                </Button>
              </>
            )}
            {session.status === 'OPEN' && !session.qc_employee_id && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleJoin}
                disabled={actionLoading}
              >
                <Play
                  size={16}
                  className="me-1"
                />{' '}
                Nhận phiên
              </Button>
            )}
            {session.status === 'IN_PROGRESS' && isAdminOrManager && (
              <Button
                variant="success"
                size="sm"
                onClick={handleComplete}
                disabled={actionLoading}
              >
                <CheckCircle
                  size={16}
                  className="me-1"
                />{' '}
                Hoàn thành Session
              </Button>
            )}
          </div>
        </div>

        <Row className="mb-3">
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <h6 className="text-muted mb-3">Thông tin Session</h6>
                <div className="mb-2">
                  <strong>Chi nhánh:</strong> {session.branch?.name || session.branch_id}
                </div>
                <div className="mb-2">
                  <strong>Loại:</strong>{' '}
                  <Badge bg={session.type === 'AUTO' ? 'info' : 'secondary'}>{SESSION_TYPE_LABELS[session.type]}</Badge>
                </div>
                <div className="mb-2">
                  <strong>Ngày phiên:</strong> {formatDate(session.planned_task_date)}
                </div>
                <div className="mb-2">
                  <strong>Cửa sổ lấy việc:</strong> {formatDate(session.review_date_from)} –{' '}
                  {formatDate(session.review_date_to)}
                </div>
                <div className="mb-2">
                  <strong>QC viên:</strong> {session.qc_employee?.full_name || 'Chưa có'}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <h6 className="text-muted mb-3">
                  Mục trong phiên: {sessionItemCount}
                  {session.items_target ? ` / ${session.items_target} dự kiến` : ''}
                </h6>
                <div className="mb-1">
                  <strong>Task ca làm:</strong> {session.shift_tasks?.length || 0}
                </div>
                <div className="mb-1">
                  <strong>Chuẩn bị bàn:</strong> {session.prep_reservations?.length || 0}
                </div>
                <div className="mb-1">
                  <strong>Kiểm tra Game:</strong> {session.game_check_sessions?.length || 0}
                </div>
                <div className="mb-1">
                  <strong>Duyệt Loyalty:</strong> {session.loyalty_participations?.length || 0}
                </div>
                <div className="mt-3 pt-3 border-top d-flex gap-3 small">
                  <span className="text-success">Đạt {stats.passed}</span>
                  <span className="text-danger">Không đạt {stats.failed}</span>
                  <span className="text-muted">Chưa chấm {stats.in_review + stats.pending}</span>
                </div>
                <div className="mt-3 pt-3 border-top">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/qc-results?session_id=${sessionId}`)}
                  >
                    Xem trong Kết quả QC
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-3">
          {[
            { label: 'Đạt', value: stats.passed || 0, color: 'success', Icon: CheckCircle },
            { label: 'Không đạt', value: stats.failed || 0, color: 'danger', Icon: XCircle },
            { label: 'Đang review', value: stats.in_review || 0, color: 'warning', Icon: Clock },
            { label: 'Chờ review', value: stats.pending || 0, color: 'secondary', Icon: Clock },
          ].map(({ label, value, color, Icon }) => (
            <Col
              key={label}
              md={3}
            >
              <Card className={`h-100 border-${color}`}>
                <Card.Body className="text-center">
                  <Icon
                    size={24}
                    className={`text-${color} mb-2`}
                  />
                  <h4 className={`text-${color} mb-0`}>{value}</h4>
                  <small className="text-muted">{label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <h6 className="mb-3">Danh sách Tasks đã Review ({pagination.total || 0})</h6>
        {loadingTasks ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : reviewedTasks.length === 0 ? (
          <div className="text-center py-4 text-muted">Chưa có task nào được review trong session này.</div>
        ) : (
          <>
            <div className="table-responsive">
              <Table
                striped
                bordered
                hover
                size="sm"
              >
                <thead>
                  <tr>
                    <th>Nội dung</th>
                    <th>Nguồn</th>
                    <th>Trạng thái QC</th>
                    <th>Bằng chứng</th>
                    <th>Video</th>
                    <th>Lí do thất bại</th>
                    <th>Nhân viên</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewedTasks.map((row) => (
                    <tr key={`${row.source_kind}:${row.source_id}`}>
                      <td>
                        <strong>{row.title}</strong>
                      </td>
                      <td>
                        <Badge bg="info">{SOURCE_KIND_LABELS[row.source_kind] || row.qc_type || row.source_kind}</Badge>
                      </td>
                      <td>
                        <Badge
                          bg={
                            row.qc_status === 'PASSED'
                              ? 'success'
                              : row.qc_status === 'FAILED'
                                ? 'danger'
                                : row.qc_status === 'IN_REVIEW'
                                  ? 'warning'
                                  : 'secondary'
                          }
                        >
                          {QC_STATUS_LABELS[row.qc_status] || row.qc_status || 'PENDING'}
                        </Badge>
                      </td>
                      <td>
                        {(() => {
                          const urls = row.worker_evidence?.image_urls?.length
                            ? row.worker_evidence.image_urls
                            : row.qc_evidence?.image_urls;
                          if (!urls?.length) return <span className="text-muted">-</span>;
                          return (
                            <div className="d-flex flex-wrap gap-2">
                              {urls.map((url, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-decoration-none"
                                  onClick={() => setPreviewImageUrl(url)}
                                >
                                  Ảnh {idx + 1}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const urls = row.qc_evidence?.video_urls;
                          if (!urls?.length) return <span className="text-muted">-</span>;
                          return (
                            <div className="d-flex flex-wrap gap-2">
                              {urls.map((url, idx) => (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                >
                                  Video {idx + 1}
                                </a>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <small
                          className={row.qc_review_result === 'REJECTED_AND_FIXED' ? 'text-danger' : 'text-muted'}
                        >
                          {row.qc_review_result === 'REJECTED_AND_FIXED' ? row.qc_review_notes || '-' : '-'}
                        </small>
                      </td>
                      <td>{row.worker?.full_name || '-'}</td>
                      <td>{row.qc_reviewed_at ? format(new Date(row.qc_reviewed_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {pagination.total_pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Trang {pagination.page} / {pagination.total_pages} (Tổng: {pagination.total})
                </small>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Trước
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                  >
                    Sau
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        show={!!previewImageUrl}
        onHide={() => setPreviewImageUrl(null)}
        fullscreen
      >
        <Modal.Body className="p-0 d-flex align-items-center justify-content-center bg-dark min-vh-100">
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt="Bằng chứng"
              className="img-fluid"
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
              onClick={() => setPreviewImageUrl(null)}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button
            variant="secondary"
            onClick={() => setPreviewImageUrl(null)}
          >
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
